import { styled } from '@mui/material'
import { useMemo } from 'react'
import { scrollFadeMask } from '@omnea-app/omui/scrollFadeMask'
import { NavList } from './NavList'
import { SideNavContext } from './SideNavContext'
import { useScrollEdges } from './useScrollEdges'
import type { ReactNode } from 'react'

// In rem like the items inside, so a larger browser text size widens the nav with them instead of clipping
// the icon rail.
const SIDE_NAV_WIDTH = { expanded: '12.5rem', wide: '15rem', collapsed: '3.5rem' } as const

const expandedWidth = (wide: boolean) => (wide ? SIDE_NAV_WIDTH.wide : SIDE_NAV_WIDTH.expanded)

const TOP_FADE = '2rem'
const BOTTOM_FADE = '4rem'

type SideNavProps = {
  'aria-label': string
  /** Stays above the scrolling items, e.g. the logo and Chat. */
  header?: ReactNode
  children: ReactNode
  /** Stays below the scrolling items, e.g. Settings and New request. */
  footer?: ReactNode
  /** Shrinks the nav to its icon rail. */
  collapsed?: boolean
  /** 240px rather than 200px when expanded, for longer labels such as the settings areas. */
  wide?: boolean
}

type OwnerState = { collapsed: boolean; wide: boolean }

const shouldForwardProp = (prop: PropertyKey) => prop !== 'ownerState'

const Root = styled('nav', { shouldForwardProp })<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  width: ownerState.collapsed ? SIDE_NAV_WIDTH.collapsed : expandedWidth(ownerState.wide),
  height: '100%',
  overflowX: 'hidden',
  backgroundColor: theme.palette.bg.surface.subtle,
  borderRight: `${theme.strokeWidth.default} solid ${theme.palette.border.subtle}`,
  transition: theme.transitions.create('width', { duration: theme.transitions.duration.short }),
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
}))

// On the rail, the padding that centres a 2.25rem item in the collapsed width.
const sidePadding = (collapsed: boolean) => (collapsed ? 2.5 : 3)

// While the nav widens, its contents already have their expanded width, so labels are revealed rather
// than wrapping mid-animation.
const expandedMinWidth = ({ collapsed, wide }: OwnerState) => (collapsed ? 0 : expandedWidth(wide))

const Header = styled('div', { shouldForwardProp })<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  padding: theme.spacing(0, sidePadding(ownerState.collapsed)),
  minWidth: expandedMinWidth(ownerState),
}))

const Footer = styled('div', { shouldForwardProp })<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  gap: theme.spacing(4),
  padding: theme.spacing(3, sidePadding(ownerState.collapsed), 4),
  minWidth: expandedMinWidth(ownerState),
}))

const Scroller = styled('div', { shouldForwardProp })<{ ownerState: OwnerState & { atStart: boolean; atEnd: boolean } }>(
  ({ theme, ownerState }) => ({
    flex: 1,
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    // A classic scrollbar would take a third of the icon rail; the edge fades show there is more instead.
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    // The design keeps a list scrolled to its end well clear of the footer's Settings.
    padding: theme.spacing(3, sidePadding(ownerState.collapsed), 18),
    maskImage: scrollFadeMask({
      start: ownerState.atStart ? '0px' : TOP_FADE,
      end: ownerState.atEnd ? '0px' : BOTTOM_FADE,
      fadeSize: BOTTOM_FADE,
      direction: 'to bottom',
    }),
  })
)

const Items = styled(NavList, { shouldForwardProp })<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  minWidth: ownerState.collapsed ? 0 : `calc(${expandedWidth(ownerState.wide)} - 2 * ${theme.spacing(sidePadding(false))})`,
}))

/**
 * The app's side navigation: a pinned header, a list of `NavItem`s and `NavGroup`s that scrolls between
 * them and fades out at whichever edge has more to see, and a pinned footer. Collapsed, it is an icon rail
 * and every item inside follows.
 */
export const SideNav = ({ 'aria-label': ariaLabel, header, children, footer, collapsed = false, wide = false }: SideNavProps) => {
  const { setScroller, setContent, atStart, atEnd } = useScrollEdges()
  const context = useMemo(() => ({ collapsed }), [collapsed])
  const ownerState = { collapsed, wide }

  return (
    <SideNavContext.Provider value={context}>
      <Root aria-label={ariaLabel} ownerState={ownerState}>
        {header != null && <Header ownerState={ownerState}>{header}</Header>}
        <Scroller ref={setScroller} ownerState={{ ...ownerState, atStart, atEnd }}>
          <Items ref={setContent} ownerState={ownerState}>
            {children}
          </Items>
        </Scroller>
        {footer != null && <Footer ownerState={ownerState}>{footer}</Footer>}
      </Root>
    </SideNavContext.Provider>
  )
}
